import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  ListItemIcon,
  Divider,
  Container,
  Stack,
  Drawer,
  FormControl,
  Select,
  MenuItem as MuiMenuItem,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon as MuiListItemIcon,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Person,
  Logout,
  Close,
  Description,
  AttachMoney,
  Help,
  CameraAlt,
  AutoAwesome,
  Groups,
  MenuBook,
} from '@mui/icons-material';
import GlobalStyles from '@mui/material/GlobalStyles';
import { ThemeProvider } from '@mui/material/styles';
import { useLocation, useNavigate } from 'react-router-dom';
import { AppErrorBoundary } from './AppErrorBoundary';
import { marketingTheme } from '../theme/marketingTheme';
import { marketingLinks } from '../constants/marketingEvent';
import { loadMarketingFonts } from '../theme/loadMarketingFonts';
import {
  marketingColor,
  marketingFont,
  marketingGrid,
  marketingKeyframes,
  marketingLayout,
  marketingLine,
} from '../theme/marketingTokens';
import { AnnouncementBar, CallWidget } from './marketing';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { useLocale } from '../contexts/LocaleContext';
import { useHelpCenterEnabled } from '../hooks/useHelpCenterEnabled';

interface PublicLayoutProps {
  children: React.ReactNode;
}

/** Header + footer nav links: JetBrains Mono, uppercase, accent on hover. */
const navLinkSx = {
  fontFamily: marketingFont.mono,
  fontSize: 12,
  fontWeight: 500,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  whiteSpace: 'nowrap',
  minWidth: 0,
  px: 0,
  color: marketingColor.textSecondary,
  '&:hover': {
    color: marketingColor.accent,
    backgroundColor: 'transparent',
  },
} as const;

const PublicLayout: React.FC<PublicLayoutProps> = ({ children }) => {
  const { currentUser, logout } = useAuth();
  const { language, region, setLanguage, setRegion } = useLocale();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation() as { t: (key: string) => string };
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(null);
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  const isCanadaDomain = useMemo(
    () => hostname.endsWith('.ca') || hostname.includes('motzulabs.ca'),
    [hostname]
  );
  const currentMarket = isCanadaDomain ? (language === 'fr' ? 'CA_FR' : 'CA_EN') : 'US_EN';

  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
      handleUserMenuClose();
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const handleLogin = () => {
    navigate('/login');
  };

  const handleSignUp = () => {
    navigate('/signup');
  };

  const handleDashboard = () => {
    navigate('/app/dashboard');
    handleUserMenuClose();
  };

  const handleMobileMenuToggle = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuOpen(false);
  };

  const handleMobileNavigation = (path: string) => {
    navigate(path);
    handleMobileMenuClose();
  };

  const redirectToHost = (targetHost: string, targetLanguage: 'en' | 'fr') => {
    if (typeof window === 'undefined') return;

    const { protocol, pathname, search, hash, hostname: currentHost } = window.location;
    if (currentHost === targetHost) return;
    const params = new URLSearchParams(search);
    params.set('lang', targetLanguage);
    const nextSearch = params.toString();
    window.location.href = `${protocol}//${targetHost}${pathname}${nextSearch ? `?${nextSearch}` : ''}${hash}`;
  };

  const handleMarketChange = (value: string) => {
    if (value === 'US_EN') {
      setRegion('US');
      setLanguage('en');
      if (isCanadaDomain) {
        redirectToHost('motzulabs.com', 'en');
      }
      return;
    }

    if (value === 'CA_EN') {
      setRegion('CA');
      setLanguage('en');
      if (!isCanadaDomain) {
        redirectToHost('motzulabs.ca', 'en');
      }
      return;
    }

    setRegion('CA');
    setLanguage('fr');
    if (!isCanadaDomain) {
      redirectToHost('motzulabs.ca', 'fr');
    }
  };

  useEffect(() => {
    if (isCanadaDomain) {
      if (region !== 'CA') setRegion('CA');
      return;
    }

    if (region !== 'US') setRegion('US');
    if (language !== 'en') setLanguage('en');
  }, [isCanadaDomain, language, region, setLanguage, setRegion]);

  // Help Center (flag `help_center`): the link exists only while the flag is on.
  const { enabled: helpCenterEnabled } = useHelpCenterEnabled();

  const navigationItems = [
    { label: t('public.nav.features'), path: '/features', icon: <AutoAwesome /> },
    { label: t('public.nav.pricing'), path: '/pricing', icon: <AttachMoney /> },
    { label: t('public.nav.productDemos'), path: '/product-demos', icon: <CameraAlt /> },
    { label: t('public.nav.documentation'), path: '/docs', icon: <Description /> },
    // Desktop header shows it from `lg` up: one more link overflows the header between md and lg.
    // The mobile drawer and the footer always carry it.
    ...(helpCenterEnabled
      ? [{ label: t('public.nav.helpCenter'), path: '/help', icon: <MenuBook />, desktopFrom: 'lg' as const }]
      : []),
    // About and Contact sit last as the "about the company" pair; /about was
    // previously reachable only from the footer.
    { label: t('public.nav.about'), path: '/about', icon: <Groups /> },
    { label: t('public.nav.contact'), path: '/contact', icon: <Help /> },
  ];

  // Fetch the marketing faces only while a public page is mounted.
  useEffect(() => {
    loadMarketingFonts();
  }, []);

  return (
    <ThemeProvider theme={marketingTheme}>
      {/* Support widget: public marketing pages only (unmounts with this layout). */}
      <CallWidget />
      {/*
        `CssBaseline` in App.tsx paints the body from the app theme. Repaint it
        for as long as a public page is mounted so overscroll and any gap below
        the layout stay on the marketing ink rather than flashing app grey.
      */}
      <GlobalStyles
        styles={{
          body: { backgroundColor: marketingColor.ink, color: marketingColor.text },
          // Declared once here so every marketing section can reference them.
          ...marketingKeyframes,
        }}
      />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
          backgroundColor: marketingColor.ink,
          // 64px hairline grid from the marketing handoff.
          backgroundImage: marketingGrid,
          color: marketingColor.text,
          // Deliberately not a bare `& a`: that repainted every link on every
          // public page, including legal and docs prose where brand green is
          // wrong. Links opt in by not setting their own colour.
          '& a:not([class*="Mui"]):not([data-plain])': {
            color: marketingColor.accent,
          },
          '& a:not([class*="Mui"]):not([data-plain]):hover': {
            color: marketingColor.accentHover,
          },
        }}
      >
      <AnnouncementBar
        onBookMeeting={() =>
          window.open(marketingLinks.scheduler, '_blank', 'noopener,noreferrer')
        }
      />

      {/* Header */}
      <AppBar position="sticky" sx={{ top: 0, zIndex: 30 }}>
        <Toolbar
          sx={{
            minHeight: { xs: 64, md: marketingLayout.headerHeight },
            maxWidth: marketingLayout.maxWidth,
            width: '100%',
            mx: 'auto',
            px: { xs: 2, md: 6 },
          }}
        >
          {/* Mobile Menu Button - positioned first */}
          <IconButton
            color="inherit"
            aria-label="open mobile menu"
            onClick={handleMobileMenuToggle}
            sx={{ 
              display: { xs: 'block', md: 'none' }, 
              mr: 2,
              '&:hover': {
                backgroundColor: 'rgba(95,226,138,0.12)',
              }
            }}
          >
            <MenuIcon />
          </IconButton>

          {/* Logo */}
          <Box sx={{ display: 'flex', alignItems: 'center', mr: { md: 3, lg: 4 }, flexShrink: 0 }}>
            <img 
              src="/logo-white.svg" 
              alt="Motzu Labs Logo" 
              style={{ 
                height: '22px', 
                width: 'auto',
                maxWidth: '120px',
                cursor: 'pointer'
              }}
              onClick={() => navigate('/')}
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
                const parent = target.parentElement;
                if (parent) {
                  parent.innerHTML = `<span style="font-family: ${marketingFont.display}; font-weight: 700; font-size: 1.25rem; color: ${marketingColor.text}; cursor: pointer;">Motzu Labs</span>`;
                }
              }}
            />
          </Box>
          
          {/* Navigation Links - Desktop */}
          <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: { md: 2, lg: 3 }, mr: 2 }}>
            {navigationItems.map((item) => (
              <Button
                key={item.path}
                color="inherit"
                onClick={() => navigate(item.path)}
                sx={{
                  ...navLinkSx,
                  ...('desktopFrom' in item ? { display: { md: 'none', lg: 'inline-flex' } } : null),
                  ...(location.pathname === item.path || location.pathname.startsWith(`${item.path}/`)
                    ? { color: marketingColor.accent }
                    : null),
                }}
              >
                {item.label}
              </Button>
            ))}
          </Box>
          
          <Box sx={{ flexGrow: 1 }} />

          {/* Market Selector */}
          <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', mr: 1 }}>
            <FormControl size="small" sx={{ minWidth: 132 }}>
              <Select
                value={currentMarket}
                onChange={(event) => handleMarketChange(event.target.value)}
                sx={{
                  '& .MuiSelect-select': {
                    py: 0.75,
                    fontFamily: marketingFont.mono,
                    fontSize: 12,
                    fontWeight: 500,
                    letterSpacing: '0.04em',
                    color: marketingColor.textSecondary,
                  },
                  '& .MuiSelect-icon': { color: marketingColor.textMuted },
                }}
              >
                <MuiMenuItem value="US_EN">🇺🇸 US - EN</MuiMenuItem>
                <MuiMenuItem value="CA_EN">🇨🇦 CAN - EN</MuiMenuItem>
                <MuiMenuItem value="CA_FR">🇨🇦 CAN - FR</MuiMenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* Auth Buttons / User Menu */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {currentUser ? (
              <>
                <IconButton
                  size="large"
                  edge="end"
                  aria-label="account of current user"
                  aria-controls="primary-search-account-menu"
                  aria-haspopup="true"
                  onClick={handleUserMenuOpen}
                  color="inherit"
                >
                  <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
                    {currentUser.email?.charAt(0).toUpperCase()}
                  </Avatar>
                </IconButton>
                <Menu
                  anchorEl={userMenuAnchor}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                  }}
                  keepMounted
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  open={Boolean(userMenuAnchor)}
                  onClose={handleUserMenuClose}
                >
                  <MenuItem onClick={handleDashboard}>
                    <ListItemIcon>
                      <Person fontSize="small" />
                    </ListItemIcon>
                    {t('public.user.dashboard')}
                  </MenuItem>
                  <Divider />
                  <MenuItem onClick={handleLogout}>
                    <ListItemIcon>
                      <Logout fontSize="small" />
                    </ListItemIcon>
                    {t('public.user.logout')}
                  </MenuItem>
                </Menu>
              </>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>
                <Button variant="text" onClick={handleLogin} sx={navLinkSx}>
                  {t('public.nav.signIn')}
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSignUp}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  {t('public.nav.getStarted')}
                </Button>
              </Box>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      {/* Mobile Navigation Drawer */}
      <Drawer
        anchor="right"
        open={mobileMenuOpen}
        onClose={handleMobileMenuClose}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            width: 280,
            boxSizing: 'border-box',
            backgroundImage: marketingGrid,
            borderLeft: `1px solid ${marketingLine.hairline}`,
          },
        }}
      >
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography
              variant="h6"
              component="div"
              sx={{
                fontFamily: marketingFont.mono,
                fontSize: 12,
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                color: marketingColor.textMuted,
              }}
            >
              {t('public.nav.menu')}
            </Typography>
            <IconButton onClick={handleMobileMenuClose}>
              <Close />
            </IconButton>
          </Box>
          
          <List>
            {navigationItems.map((item) => (
              <ListItem key={item.label} disablePadding>
                <ListItemButton
                  onClick={() => handleMobileNavigation(item.path)}
                  selected={location.pathname === item.path}
                  sx={{
                    borderRadius: 1,
                    '&.Mui-selected': {
                      backgroundColor: marketingColor.surfaceSelected,
                      color: marketingColor.accent,
                    },
                  }}
                >
                  <MuiListItemIcon sx={{ color: 'inherit', minWidth: 40 }}>
                    {item.icon}
                  </MuiListItemIcon>
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{
                      fontFamily: marketingFont.mono,
                      fontSize: 12,
                      textTransform: 'uppercase',
                      letterSpacing: '0.04em',
                    }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
          
          <Divider sx={{ my: 2 }} />

          {/* Mobile Market Selector */}
          <Box sx={{ px: 2, py: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
              Market
            </Typography>
            <FormControl size="small" fullWidth>
              <Select
                value={currentMarket}
                onChange={(event) => handleMarketChange(event.target.value)}
              >
                <MuiMenuItem value="US_EN">🇺🇸 US - EN</MuiMenuItem>
                <MuiMenuItem value="CA_EN">🇨🇦 CAN - EN</MuiMenuItem>
                <MuiMenuItem value="CA_FR">🇨🇦 CAN - FR</MuiMenuItem>
              </Select>
            </FormControl>
          </Box>
          
          <Divider sx={{ my: 2 }} />
          
          {/* Mobile Auth Section */}
          <Box sx={{ p: 2 }}>
            {currentUser ? (
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {t('public.user.signedInAs')} {currentUser.displayName || currentUser.email}
                </Typography>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => {
                    handleMobileNavigation('/app/profile');
                  }}
                  sx={{ mb: 1 }}
                >
                  {t('public.user.profile')}
                </Button>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={() => {
                    handleLogout();
                    handleMobileMenuClose();
                  }}
                >
                  {t('public.user.logout')}
                </Button>
              </Box>
            ) : (
              <Box>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => {
                    handleMobileNavigation('/login');
                  }}
                  sx={{ mb: 1 }}
                >
                  {t('public.nav.signIn')}
                </Button>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={() => {
                    handleMobileNavigation('/signup');
                  }}
                >
                  {t('public.nav.getStarted')}
                </Button>
              </Box>
            )}
          </Box>
        </Box>
      </Drawer>

      {/* Main Content */}
      <Box sx={{ flex: 1 }}>
        <AppErrorBoundary resetKey={location.pathname}>
          {children}
        </AppErrorBoundary>
      </Box>

      {/* Footer */}
      <Box
        sx={{
          backgroundColor: marketingColor.ink,
          color: marketingColor.textSecondary,
          borderTop: `1px solid ${marketingLine.hairline}`,
          py: 6,
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4 }}>
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <img 
                  src="/logo-white.svg" 
                  alt="Motzu Labs Logo" 
                  style={{ 
                    height: '22px', 
                    width: 'auto',
                    maxWidth: '120px'
                  }}
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                    const parent = target.parentElement;
                    if (parent) {
                      parent.innerHTML = `<span style="font-family: ${marketingFont.display}; font-weight: 700; font-size: 1.25rem; color: ${marketingColor.text};">Motzu Labs</span>`;
                    }
                  }}
                />
              </Box>
              <Typography variant="body2" sx={{ color: marketingColor.textSecondary, mb: 2 }}>
                {t('public.footer.tagline')}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                {/* Social icons are omitted until there are accounts to point
                    them at — three buttons that do nothing read as broken. */}
              </Box>
            </Box>
            <Box sx={{ minWidth: '150px' }}>
              <Typography variant="h6" gutterBottom sx={{ color: marketingColor.text }}>{t('public.footer.product')}</Typography>
              <Stack spacing={1}>
                <Button color="inherit" sx={{ ...navLinkSx, justifyContent: 'flex-start', p: 0 }} onClick={() => navigate('/product-demos')}>{t('public.nav.productDemos')}</Button>
                <Button color="inherit" sx={{ ...navLinkSx, justifyContent: 'flex-start', p: 0 }} onClick={() => navigate('/features')}>{t('public.footer.features')}</Button>
                <Button color="inherit" sx={{ ...navLinkSx, justifyContent: 'flex-start', p: 0 }} onClick={() => navigate('/pricing')}>{t('public.footer.pricing')}</Button>
                <Button color="inherit" sx={{ ...navLinkSx, justifyContent: 'flex-start', p: 0 }} onClick={() => navigate('/docs')}>{t('public.footer.apiDocs')}</Button>
                {helpCenterEnabled && (
                  <Button color="inherit" sx={{ ...navLinkSx, justifyContent: 'flex-start', p: 0 }} onClick={() => navigate('/help')}>{t('public.footer.helpCenter')}</Button>
                )}
              </Stack>
            </Box>
            <Box sx={{ minWidth: '150px' }}>
              <Typography variant="h6" gutterBottom sx={{ color: marketingColor.text }}>{t('public.footer.company')}</Typography>
              <Stack spacing={1}>
                <Button color="inherit" sx={{ ...navLinkSx, justifyContent: 'flex-start', p: 0 }} onClick={() => navigate('/about')}>{t('public.footer.about')}</Button>
                <Button color="inherit" sx={{ ...navLinkSx, justifyContent: 'flex-start', p: 0 }} onClick={() => navigate('/contact')}>{t('public.footer.contact')}</Button>
              </Stack>
            </Box>
            <Box sx={{ minWidth: '150px' }}>
              <Typography variant="h6" gutterBottom sx={{ color: marketingColor.text }}>{t('public.footer.legal')}</Typography>
              <Stack spacing={1}>
                <Button color="inherit" sx={{ ...navLinkSx, justifyContent: 'flex-start', p: 0 }} onClick={() => navigate('/privacy')}>{t('public.footer.privacyPolicy')}</Button>
                <Button color="inherit" sx={{ ...navLinkSx, justifyContent: 'flex-start', p: 0 }} onClick={() => navigate('/compliance')}>{t('public.footer.compliance')}</Button>
                <Button color="inherit" sx={{ ...navLinkSx, justifyContent: 'flex-start', p: 0 }} onClick={() => navigate('/terms')}>{t('public.footer.termsOfService')}</Button>
              </Stack>
            </Box>
          </Box>
          <Divider sx={{ my: 3, borderColor: marketingLine.hairline }} />
          <Typography
            variant="body2"
            sx={{ color: marketingColor.textMuted, textAlign: 'center', mb: 2 }}
          >
            {t('public.footer.trust')}
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
            <Typography
              variant="body2"
              sx={{ fontFamily: marketingFont.mono, fontSize: 11.5, color: marketingColor.textMuted }}
            >
              {t('public.footer.copyright')}
            </Typography>
            <Typography
              variant="body2"
              sx={{ fontFamily: marketingFont.mono, fontSize: 11.5, color: marketingColor.textMuted }}
            >
              {t('public.footer.builtWith')}
            </Typography>
          </Box>
        </Container>
      </Box>
      </Box>
    </ThemeProvider>
  );
};

export default PublicLayout;
